/**
 * letter-banner/index.js — «Гирлянда-растяжка из букв»: text → a flag per
 * letter, previewed hanging on a string, printed at true size.
 *
 * Shares the print engine with /printable-masks: the path dialect and the
 * sheet planner (printable-masks/geom.js, sheet.js), the PDF operators
 * (printable-masks/pdf.js) and the holiday calendar (catalog.js). Spacer
 * flags can carry a mask character, drawn by the same compose.js.
 */
import { holidaysFor, THEMES } from '../printable-masks/catalog.js';
import { build, paint, toSvg } from '../printable-masks/compose.js';
import { planSheets, PAPERS, MARGIN, FOOT, GAP } from '../printable-masks/sheet.js';
import { ensurePdfLib } from '../printable-masks/pdf.js';
import { SIZES, SHAPES, TAB, flagOps, flagsFromText, capHeight, mix } from './flags.js';
import { buildBannerPdf } from './pdf.js';

const SLUG = 'letter-banner';
const CFG = window.__BANNER__ || {};
const T = CFG.t || {};
const t = (k, fb) => (T[k] != null ? T[k] : (fb != null ? fb : k));
const $ = id => document.getElementById(id);
const LOCALE = window.__LOCALE || 'en';

export const PALETTES = {
    rainbow: ['#e63b2e', '#f58a1f', '#ffcf33', '#8bc34a', '#4cc9f0', '#3f6fe0', '#8e5cf0'],
    pastel: ['#f7b6c2', '#ffd8a8', '#fff1a8', '#c8e9a0', '#b5e3f0', '#c9c0f5'],
    party: ['#ff5fa2', '#ffcf33', '#4cc9f0', '#8bc34a', '#8e5cf0', '#f58a1f'],
    gold: ['#2b2b2e', '#d4a72c', '#2b2b2e', '#f4efe2'],
    winter: ['#d63b3b', '#2e9e5b', '#f4efe2', '#d4a72c'],
    autumn: ['#d9772b', '#f2b84b', '#8b5a2b', '#c9712a', '#e6b450'],
    halloween: ['#f58a1f', '#2b2b2e', '#7b4bd1', '#8bc34a'],
    spring: ['#ff8fc7', '#c8e9a0', '#ffe066', '#b5e3f0'],
    sea: ['#1f6fb2', '#4cc9f0', '#f4efe2', '#f7a21b'],
    kraft: ['#d9b38c', '#c49a6c'],
};
const PATTERNS = ['none', 'dots', 'stripes', 'stars', 'confetti', 'border'];
const SPACERS = ['star', 'heart', 'balloon', 'snowflake', 'flower', 'chars', 'blank', 'gap'];
const FONTS = ['montserrat', 'russoone', 'lobster', 'comfortaa', 'caveat', 'oswald', 'pacifico', 'marker', 'bebas'];
const BOLD = { montserrat: true, comfortaa: true, caveat: true, oswald: true };
const HOLIDAY_STYLE = {
    newyear: ['winter', 'snowflake', 'winter'], halloween: ['halloween', 'chars', 'halloween'], autumn: ['autumn', 'chars', 'forest'],
    muertos: ['party', 'flower', 'halloween'], carnival: ['rainbow', 'star', 'zoo'], maslenitsa: ['rainbow', 'flower', 'farm'],
    spring: ['spring', 'flower', 'spring'], childrensday: ['rainbow', 'balloon', 'zoo'], easter: ['spring', 'chars', 'spring'],
    lunar: ['winter', 'chars', 'zoo'], agustus: ['party', 'star', 'zoo'],
};
const MAX_FLAGS = 60;

const state = {
    text: '', upper: true, shape: 'triangle', size: 's', hang: 'tab', mode: 'color',
    palette: 'rainbow', pattern: 'none', font: 'montserrat', spacer: 'star', charTheme: 'zoo',
    paper: 'a4', photos: [], holiday: null,
};

const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* ── glyphs (async, cached per face) ─────────────────────────────────── */

const glyphCache = new Map();
let fontReady = null;
function TF() { return window.PdfTextFont; }

async function faceFor(text) {
    const tf = TF();
    if (!tf) return null;
    // A failed table load is not remembered: the next render asks again (a
    // 502 on a flaky network must not leave the banner letterless for good).
    if (!fontReady) fontReady = tf.load().catch(e => { fontReady = null; throw e; });
    await fontReady;
    if (tf.needsCjk && tf.needsCjk(text) && tf.loadCjk) await tf.loadCjk();
    if (tf.canRender(text, state.font)) return state.font;
    return tf.pick(text) || 'noto';
}

async function glyphsFor(chars, face) {
    const tf = TF();
    const out = {};
    await Promise.all(chars.map(async ch => {
        const key = face + '|' + ch;
        if (!glyphCache.has(key)) {
            glyphCache.set(key, tf.outline(face, ch, 100, { bold: !!BOLD[face] }).then(r => (r && r.path) || '')
                .catch(e => { glyphCache.delete(key); throw e; }));
        }
        out[ch] = await glyphCache.get(key);
    }));
    return out;
}

/* ── the banner model ────────────────────────────────────────────────── */

let built = null;          // { flags: [{ops, H, W, top, photoBox, photo, number}], n, face }
let buildSeq = 0;

function themeChars() {
    const th = THEMES.find(x => x.id === state.charTheme) || THEMES[0];
    return th.items || ['hare'];
}

async function model() {
    const seq = ++buildSeq;
    const items = flagsFromText(state.text, { upper: state.upper, locale: (window.__intlLocale && window.__intlLocale()) || undefined }).slice(0, MAX_FLAGS);
    const letters = items.filter(i => i.kind === 'letter').map(i => i.ch);
    let face = state.font, glyphs = {}, fontFailed = false;
    if (letters.length) {
        try {
            if (!TF()) throw new Error('PdfTextFont not loaded');
            face = await faceFor(letters.join(''));
            glyphs = await glyphsFor(Array.from(new Set(letters)), face);
        } catch (e) { fontFailed = true; }
    }
    if (seq !== buildSeq) return null;          // a newer edit superseded this one
    const refH = capHeight(Object.values(glyphs));
    const pal = PALETTES[state.palette] || PALETTES.rainbow;
    const W = SIZES[state.size] || SIZES.s;
    const chars = themeChars();
    const photos = state.photos.slice();
    const flags = [];
    let ci = 0, pi = 0, colour = 0;
    const push = (content, extra) => {
        const fill = pal[colour++ % pal.length];
        const iconColour = content && content.kind === 'icon' ? (pal.find(c => c !== fill) || '#ffffff') : null;
        if (content && content.kind === 'icon') content.colour = mix(iconColour, '#ffffff', 0.15);
        const f = flagOps({ shape: state.shape, W, hang: state.hang, fill, pattern: state.pattern, seed: flags.length + 1,
            line: state.mode === 'line', content });
        flags.push(Object.assign(f, extra || {}));
    };
    const spacer = (between) => {
        const mark = between ? { spacerFlag: true } : null;
        if (pi < photos.length) { push({ kind: 'photo' }, Object.assign({ photo: photos[pi++] }, mark)); return; }
        if (state.spacer === 'gap') { flags.push({ gap: true }); return; }
        if (state.spacer === 'blank') { push(null, mark); return; }
        if (state.spacer === 'chars') {
            const id = chars[ci++ % chars.length];
            const painted = paint(build(id, { format: 'band' }), { mode: state.mode === 'line' ? 'line' : 'color', cut: 'detail', scale: 1 });
            push({ kind: 'char', painted }, mark);
            return;
        }
        push({ kind: 'icon', name: state.spacer }, mark);
    };
    // Photos left over after the word gaps hang at both ends.
    const gaps = items.filter(i => i.kind === 'gap').length;
    const extra = Math.max(0, photos.length - gaps);
    for (let k = 0; k < Math.ceil(extra / 2); k++) spacer();
    for (const it of items) {
        if (it.kind === 'letter') push({ kind: 'letter', d: glyphs[it.ch] || '', refH });
        else if (it.kind === 'icon') push({ kind: 'icon', name: it.name });
        else spacer(true);
    }
    while (pi < photos.length) spacer();
    let n = 0;
    for (const f of flags) if (!f.gap) f.number = ++n;
    return { flags, n, face, W, fontFailed };
}

/* ── preview: flags hanging on a string ──────────────────────────────── */

function flagSvg(f, x, y, rot) {
    // On the string the tab is folded behind it: the preview shows the flag as
    // it hangs, the paper keeps the tab and its fold line (paperOnly).
    const painted = { ops: f.ops.filter(o => !o.paperOnly), box: { x0: 0, y0: 0, x1: f.W, y1: f.H }, scale: 1 };
    let svg = toSvg(painted, {});
    if (f.photo && f.photoBox) {
        const id = 'lbp' + Math.random().toString(36).slice(2, 8);
        const b = f.photoBox;
        svg = svg.replace(/<\/svg>$/, '<clipPath id="' + id + '"><path d="' + b.clip + '"/></clipPath><image href="' + f.photo.url
            + '" x="' + (b.cx - b.r) + '" y="' + (b.cy - b.r) + '" width="' + 2 * b.r + '" height="' + 2 * b.r
            + '" preserveAspectRatio="xMidYMid slice" clip-path="url(#' + id + ')"/><path d="' + b.clip + '" fill="none" stroke="#2a211b" stroke-width="0.6"/></svg>');
    }
    svg = svg.replace('<svg ', '<svg x="' + (-f.W / 2) + '" y="0" width="' + f.W + '" height="' + f.H + '" ');
    return '<g transform="translate(' + x.toFixed(1) + ' ' + y.toFixed(1) + ') rotate(' + rot.toFixed(2) + ')">' + svg + '</g>';
}

function previewSvg(m) {
    const W = m.W, step = W + Math.max(14, W * 0.18);
    const slots = m.flags;
    if (!slots.length) return '';
    // Rows break between words, never inside one: a long banner previews as
    // several strings, each holding whole words (up to ~12 flags).
    const cap = Math.max(12, Math.ceil(slots.length / Math.ceil(slots.length / 12)));
    const words = [];
    let cur = [];
    for (const f of slots) {
        cur.push(f);
        if (f.spacerFlag || f.gap) { words.push(cur); cur = []; }
    }
    if (cur.length) words.push(cur);
    const rows = [];
    let row = [];
    for (const w of words) {
        if (row.length && row.length + w.length > cap) { rows.push(row); row = []; }
        row = row.concat(w);
    }
    if (row.length) rows.push(row);
    const perRow = Math.max(...rows.map(r => r.length));
    const H = Math.max(...slots.filter(f => !f.gap).map(f => f.H), W);
    const rowW = perRow * step + step * 0.6, sag = W * 0.35, rowH = H + TAB + sag + W * 0.4;
    let body = '';
    rows.forEach((row, r) => {
        const y0 = r * rowH + TAB + 4;
        const L = row.length * step + step * 0.6;
        const x0 = (rowW - L) / 2;
        const yAt = x => y0 + sag * (1 - Math.pow(2 * (x - x0) / L - 1, 2));
        body += '<path d="M' + (x0 - 6) + ' ' + y0 + ' Q' + (x0 + L / 2) + ' ' + (y0 + 2 * sag) + ' ' + (x0 + L + 6) + ' ' + y0 + '" fill="none" stroke="#7a6a58" stroke-width="' + (W * 0.012 + 0.6) + '"/>';
        row.forEach((f, i) => {
            if (f.gap) return;
            const x = x0 + step * 0.8 + i * step;
            const slope = Math.atan2(-4 * sag * (2 * (x - x0) / L - 1) / L * 2 / 2, 1) * 180 / Math.PI;
            body += flagSvg(f, x, yAt(x), slope * 0.6);
        });
    });
    const vbH = rows.length * rowH;
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + rowW.toFixed(1) + ' ' + vbH.toFixed(1) + '" role="img" aria-label="' + esc(t('preview_label', 'Banner')) + '">' + body + '</svg>';
}

/* ── layout on paper ─────────────────────────────────────────────────── */

function pieces(m) {
    return m.flags.filter(f => !f.gap).map(f => ({ kind: 'flag', flag: f, w: f.W, h: f.H - f.top }));
}

function bestOrient(ps) {
    if (!ps.length) return 'portrait';
    const w = ps[0].w, h = Math.max(...ps.map(p => p.h));
    const p = PAPERS[state.paper] || PAPERS.a4;
    const fit = (aw, ah) => Math.floor((aw + GAP) / (w + GAP)) * Math.floor((ah + GAP) / (h + GAP));
    const port = fit(p.w - 2 * MARGIN, p.h - 2 * MARGIN - FOOT), land = fit(p.h - 2 * MARGIN, p.w - 2 * MARGIN - FOOT);
    return land > port ? 'landscape' : 'portrait';
}

/* ── render ──────────────────────────────────────────────────────────── */

let firstRender = true;
async function render() {
    const m = await model();
    if (!m) return;
    built = m;
    const box = $('gbPreview');
    box.innerHTML = m.n ? previewSvg(m) : '<p class="gb-empty">' + esc(t('empty_text', '')) + '</p>';
    const ps = pieces(m);
    const orient = bestOrient(ps);
    const pages = ps.length ? planSheets(ps, state.paper, orient) : [];
    const strLen = m.flags.reduce((a, f) => a + (f.gap ? m.W * 0.6 : m.W + Math.max(14, m.W * 0.18)), 0) / 1000 + 0.6;
    $('gbCap').innerHTML = '<span><b>' + esc(t('flags_line', 'Flags: :n').replace(':n', m.n)) + '</b></span><span>'
        + esc(t('sheets_line', 'Sheets: :n').replace(':n', pages.length)) + '</span><span>'
        + esc(t('length_line', '≈ :m m of string').replace(':m', fmt1(strLen))) + '</span>';
    $('gbTooMany').hidden = flagsFromText(state.text, { upper: state.upper }).length <= MAX_FLAGS;
    // A banner without its letters is not worth printing: the reason stands
    // under the preview, written at render time (a disabled button gets no click).
    const ready = m.n && !m.fontFailed;
    $('gbDlPdf').disabled = !ready;
    $('gbDlPdf').title = !m.n ? t('empty_text', '') : (m.fontFailed ? t('warn_font', '') : '');
    $('gbFontWarn').hidden = !m.fontFailed;
    if (m.fontFailed) { clearTimeout(render._retry); render._retry = setTimeout(schedule, 4000); }
    $('gbSumText').textContent = state.text.slice(0, 24);
    $('gbSumFlags').textContent = [t('shape_' + state.shape), t('size_' + state.size)].join(' · ');
    $('gbSumLook').textContent = t('pal_' + state.palette);
    $('gbSumPhoto').textContent = state.photos.length ? t('photos_line', 'Photos: :n').replace(':n', state.photos.length) : '';
    $('gbCharTheme').closest('.gb-field').hidden = state.spacer !== 'chars';
    if (firstRender) { firstRender = false; try { if (window.__stage) window.__stage('built'); } catch (e) { } }
}
const fmt1 = v => (Math.round(v * 10) / 10).toLocaleString((window.__intlLocale && window.__intlLocale()) || undefined);

let renderTimer = 0;
const schedule = () => { clearTimeout(renderTimer); renderTimer = setTimeout(() => { render().catch(err => { if (window.__handleError) window.__handleError(err, 'banner_render', { say: t('err_render') }); }); }, 120); };

/* ── controls ────────────────────────────────────────────────────────── */

function segOn(attr, v) {
    document.querySelectorAll('[' + attr + ']').forEach(b => {
        const on = b.getAttribute(attr) === v;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', String(on));
    });
}
const TS = () => window.__toolSettings;
const remember = (k, v) => { try { if (TS()) TS().save(SLUG, k, v); } catch (e) { } };
const recall = (k, d) => { try { return TS() ? TS().load(SLUG, k, d) : d; } catch (e) { return d; } };

function renderChoices() {
    $('gbPalettes').innerHTML = Object.keys(PALETTES).map(id => '<button type="button" class="gb-pal' + (id === state.palette ? ' on' : '') + '" data-gbpal="' + id + '" aria-pressed="' + (id === state.palette) + '" title="' + esc(t('pal_' + id)) + '">'
        + '<b class="gb-sw">' + PALETTES[id].slice(0, 5).map(c => '<i style="background:' + c + '"></i>').join('') + '</b><span>' + esc(t('pal_' + id)) + '</span></button>').join('');
    $('gbPatterns').innerHTML = PATTERNS.map(id => '<button type="button" class="tc-seg-b' + (id === state.pattern ? ' on' : '') + '" data-gbpat="' + id + '">' + esc(t('pat_' + id)) + '</button>').join('');
    $('gbSpacer').innerHTML = SPACERS.map(id => '<option value="' + id + '"' + (id === state.spacer ? ' selected' : '') + '>' + esc(t('sp_' + id)) + '</option>').join('');
    $('gbCharTheme').innerHTML = THEMES.filter(th => th.items).map(th => '<option value="' + th.id + '"' + (th.id === state.charTheme ? ' selected' : '') + '>' + th.icon + ' ' + esc(t('theme_' + th.id)) + '</option>').join('');
    renderFonts();
}

async function renderFonts() {
    const sel = $('gbFont');
    const tf = TF();
    let usable = FONTS;
    if (tf) {
        try { if (!fontReady) fontReady = tf.load(); await fontReady; usable = FONTS.filter(id => tf.canRender(flagsFromText(state.text).filter(i => i.kind === 'letter').map(i => i.ch).join('') || 'A', id)); } catch (e) { }
    }
    if (!usable.length) usable = ['noto'];
    if (!usable.includes(state.font)) state.font = usable[0];
    sel.innerHTML = usable.map(id => {
        const meta = tf && tf.faceMeta ? tf.faceMeta(id) : null;
        return '<option value="' + id + '"' + (id === state.font ? ' selected' : '') + '>' + esc((meta && meta.label) || id) + '</option>';
    }).join('');
}

function occasionChips() {
    const hs = holidaysFor(LOCALE);
    const year = new Date().getFullYear() + (new Date().getMonth() >= 8 ? 1 : 0);
    const chips = hs.filter(h => T['text_' + h.id]).map(h => ({ id: 'hol:' + h.id, label: t('hol_' + h.id), text: t('text_' + h.id), h }));
    for (const o of ['birthday', 'welcome', 'congrats', 'graduation', 'thanks']) chips.push({ id: 'occ:' + o, label: t('occ_' + o), text: t('text_' + o).replace(':year', String(year)) });
    $('gbOccasions').innerHTML = chips.map((c, i) => '<button type="button" class="gb-occ' + (c.h ? ' hol' : '') + '" data-gbocc="' + i + '">' + esc(c.h ? t('hol_soon', ':name').replace(':name', c.label) : c.label) + '</button>').join('');
    $('gbOccasions')._chips = chips;
    return chips;
}

function applyOccasion(c) {
    state.text = c.text;
    $('gbText').value = c.text;
    if (c.h && HOLIDAY_STYLE[c.h.id]) {
        const [pal, sp, th] = HOLIDAY_STYLE[c.h.id];
        state.palette = pal; state.spacer = sp; state.charTheme = th;
    }
    renderChoices();
}

/* ── photos ──────────────────────────────────────────────────────────── */

async function addPhotos(files) {
    for (const file of files) {
        if (state.photos.length >= 20) break;
        if (!/^image\//.test(file.type || '')) continue;
        try {
            const bmp = await createImageBitmap(file);
            const s = Math.min(bmp.width, bmp.height), side = Math.min(900, s);
            const c = document.createElement('canvas'); c.width = side; c.height = side;
            const g = c.getContext('2d');
            if (!g) throw window.__canvasRefused ? window.__canvasRefused(c) : new Error('no 2d');
            g.drawImage(bmp, (bmp.width - s) / 2, (bmp.height - s) / 2, s, s, 0, 0, side, side);
            if (bmp.close) bmp.close();
            const blob = await window.__canvasBlob(c, 'image/jpeg', 0.88);
            state.photos.push({ name: file.name, url: URL.createObjectURL(blob), bytes: new Uint8Array(await blob.arrayBuffer()) });
        } catch (err) {
            if (window.__handleError) window.__handleError(err, 'banner_photo', { say: t('err_photo') });
        }
    }
    renderPhotos();
    schedule();
}
function renderPhotos() {
    $('gbPhotoList').innerHTML = state.photos.map((p, i) => '<span class="gb-ph"><img src="' + p.url + '" alt=""><button type="button" data-gbphoto="' + i + '" aria-label="' + esc(t('photo_remove')) + '">×</button></span>').join('');
    $('gbPhotoHint').hidden = !state.photos.length;
}

/* ── output ──────────────────────────────────────────────────────────── */

function outName() {
    const raw = t('file_prefix', 'banner') + '-' + (state.text || '').slice(0, 30);
    if (window.__outputName) return window.__outputName.join([window.__outputName.sanitize(raw)], 'pdf');
    return raw.replace(/[^\w\-]+/g, '-') + '.pdf';
}
const limitKey = () => 'set:' + state.shape + ':' + state.size + ':' + (state.text || '').trim().toLocaleLowerCase() + ':' + state.photos.length;

let busy = false;
async function savePdf() {
    if (busy) return;
    // The last drawing may be stale (a font that failed on a flaky network, an
    // edit still rendering): draw again before deciding, and never end a press
    // in silence — say why there is nothing to print.
    if (!built || built.fontFailed) { try { await render(); } catch (e) { } }
    if (!built || !built.n || built.fontFailed) {
        if (window.__toast) window.__toast(!built || !built.n ? t('empty_text', '') : t('warn_font', ''), 'warning');
        return;
    }
    const gate = window.__toolLimit && window.__toolLimit.begin(SLUG, limitKey());
    if (window.__toolLimit && !gate) return;
    busy = true;
    const btn = $('gbDlPdf'); const was = btn.textContent;
    btn.disabled = true; btn.textContent = t('building', '…');
    try {
        const ps = pieces(built);
        const blob = await buildBannerPdf({
            paper: state.paper, orient: bestOrient(ps), pieces: ps, total: built.n,
            footer: t('ins_' + state.hang, ''), rulerCaption: t('pdf_ruler', '50 mm'), title: outName(),
        });
        // The file exists: the download door may hold it for a second tap if the
        // build outlived the first one — the button must not keep saying «building».
        btn.disabled = false; btn.textContent = was;
        const ok = await window.__downloadFile(blob, outName());
        if (gate) { if (ok !== false) gate.commit(); else gate.abort(); }
        if (window.__aeToolComplete) window.__aeToolComplete({ ok: true, f: 'pdf', n: built.n, shape: state.shape, size: state.size, photos: state.photos.length, mode: state.mode });
    } catch (err) {
        if (gate) gate.abort();
        if (window.__handleError) window.__handleError(err, 'banner_pdf', { say: t('err_pdf') });
    } finally { busy = false; btn.disabled = !(built && built.n && !built.fontFailed); btn.textContent = was; }
}

/* ── wiring ──────────────────────────────────────────────────────────── */

function bind() {
    document.addEventListener('click', e => {
        const el = e.target.closest('[data-gbshape],[data-gbsize],[data-gbhang],[data-gbmode],[data-gbpal],[data-gbpat],[data-gbocc],[data-gbphoto],#gbDlPdf');
        if (!el || !el.closest('.gb')) return;
        const d = el.dataset;
        if (d.gbshape) { state.shape = d.gbshape; segOn('data-gbshape', d.gbshape); remember('shape', d.gbshape); }
        else if (d.gbsize) { state.size = d.gbsize; segOn('data-gbsize', d.gbsize); remember('size', d.gbsize); }
        else if (d.gbhang) { state.hang = d.gbhang; segOn('data-gbhang', d.gbhang); remember('hang', d.gbhang); }
        else if (d.gbmode) { state.mode = d.gbmode; segOn('data-gbmode', d.gbmode); remember('mode', d.gbmode); }
        else if (d.gbpal) { state.palette = d.gbpal; renderChoices(); }
        else if (d.gbpat) { state.pattern = d.gbpat; renderChoices(); }
        else if (d.gbocc != null) { const c = ($('gbOccasions')._chips || [])[+d.gbocc]; if (c) applyOccasion(c); }
        else if (d.gbphoto != null) { const p = state.photos.splice(+d.gbphoto, 1)[0]; if (p) URL.revokeObjectURL(p.url); renderPhotos(); }
        else if (el.id === 'gbDlPdf') { savePdf(); return; }
        schedule();
    });
    $('gbText').addEventListener('input', e => { state.text = e.target.value; schedule(); clearTimeout(bind._ft); bind._ft = setTimeout(renderFonts, 400); });
    $('gbUpper').addEventListener('change', e => { state.upper = e.target.checked; schedule(); });
    $('gbFont').addEventListener('change', e => { state.font = e.target.value; remember('font', state.font); schedule(); });
    $('gbSpacer').addEventListener('change', e => { state.spacer = e.target.value; schedule(); });
    $('gbCharTheme').addEventListener('change', e => { state.charTheme = e.target.value; schedule(); });
    $('gbPaperSel').addEventListener('change', e => { state.paper = e.target.value; remember('paper', state.paper); schedule(); });
    const zone = $('gbPhotos');
    if (zone && window.__bindUploaderFile) {
        window.__bindUploaderFile(zone, (f, detail) => addPhotos((detail && detail.files && detail.files.length) ? Array.from(detail.files) : [f]));
        zone.addEventListener('tool-uploader:clear', () => { state.photos.forEach(p => URL.revokeObjectURL(p.url)); state.photos = []; renderPhotos(); schedule(); });
    }
}

function init() {
    for (const [k, list, attr] of [['shape', SHAPES, 'data-gbshape'], ['size', Object.keys(SIZES), 'data-gbsize'], ['hang', ['tab', 'holes'], 'data-gbhang'], ['mode', ['color', 'line'], 'data-gbmode']]) {
        const v = recall(k, null);
        if (v && list.includes(v)) { state[k] = v; }
        segOn(attr, state[k]);
    }
    const f = recall('font', null); if (f && FONTS.includes(f)) state.font = f;
    const sp = recall('paper', null);
    state.paper = sp && PAPERS[sp] ? sp : (/-(US|CA)$/i.test(navigator.language || '') ? 'letter' : 'a4');
    $('gbPaperSel').value = state.paper;
    const chips = occasionChips();
    applyOccasion(chips.find(c => c.h) || chips[0]);
    bind();
    schedule();
    const warm = () => {
        document.querySelector('.gb').removeEventListener('pointerdown', warm, true);
        ensurePdfLib().catch(() => { });
    };
    document.querySelector('.gb').addEventListener('pointerdown', warm, true);
    try { if (window.__stage) window.__stage('ready'); } catch (e) { }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
